import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class WakeLockService {
  private wakeLock: any = null;
  private wakeLockIntervalId: any = null;
  private wakeLockElapsedMs = 0;
  private shouldKeepWakeLock = false;
  private isWakeLockRequestInFlight = false;
  public wakeLockTimerText = '00:00';
  public wakeLockStatusText = 'Wake lock idle';

  constructor() {
    document.addEventListener('visibilitychange', () => {
      console.log(`[WakeLock] page visibility changed to '${document.visibilityState}' at ${new Date().toLocaleTimeString()}`);
      if (document.visibilityState === 'visible' && this.shouldKeepWakeLock && !this.wakeLock) {
        void this.requestWakeLock();
      }
    });

    window.addEventListener('focus', () => {
      if (this.shouldKeepWakeLock && document.visibilityState === 'visible' && !this.wakeLock) {
        void this.requestWakeLock();
      }
    });
  }

  private startWakeLockTimer(): void {
    this.stopWakeLockTimer();
    this.wakeLockElapsedMs = 0;
    this.wakeLockTimerText = '00:00';
    this.wakeLockStatusText = 'Wake lock active';

    const logActiveTimer = () => {
      this.wakeLockElapsedMs += 5000;
      const totalSeconds = Math.floor(this.wakeLockElapsedMs / 1000);
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      this.wakeLockTimerText = `${minutes}:${seconds}`;
      this.wakeLockStatusText = `Wake lock active for ${this.wakeLockTimerText}`;

      console.log(`[WakeLock] active timer: ${this.wakeLockTimerText} at ${new Date().toLocaleTimeString()}`);
    };

    logActiveTimer();
    this.wakeLockIntervalId = setInterval(logActiveTimer, 5000);
  }

  private stopWakeLockTimer(): void {
    if (this.wakeLockIntervalId) {
      clearInterval(this.wakeLockIntervalId);
      this.wakeLockIntervalId = null;
    }
  }

  async requestWakeLock(): Promise<void> {
    if (this.isWakeLockRequestInFlight || this.wakeLock || !this.shouldKeepWakeLock) {
      return;
    }

    // The Screen Wake Lock API only requires the page to be visible, not focused,
    // so the lock can still be taken while the user is working in another app.
    if (document.visibilityState !== 'visible') {
      this.wakeLockStatusText = 'Wake lock waiting: page not visible';
      return;
    }

    try {
      this.isWakeLockRequestInFlight = true;
      const nav = navigator as any;
      if (!nav || !('wakeLock' in nav)) {
        this.wakeLockStatusText = 'Wake lock not supported';
        return;
      }

      this.wakeLock = await nav.wakeLock.request('screen');
      this.wakeLockStatusText = 'Wake lock activated';
      console.log(`[WakeLock] activated at ${new Date().toLocaleTimeString()}`);
      this.startWakeLockTimer();

      this.wakeLock.addEventListener('release', () => {
        const releasedAt = new Date().toLocaleTimeString();
        const activeTimeText = this.wakeLockTimerText;
        this.wakeLock = null;
        this.wakeLockStatusText = `Wake lock released at ${releasedAt}`;
        this.stopWakeLockTimer();
        this.wakeLockTimerText = '00:00';
        console.log(`[WakeLock] released at ${releasedAt}. Total active time: ${activeTimeText}`);

        if (this.shouldKeepWakeLock && document.visibilityState === 'visible') {
          void this.requestWakeLock();
        }
      });
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        this.wakeLockStatusText = 'Wake lock waiting: page not visible';
        return;
      }

      this.wakeLockStatusText = 'Wake lock failed';
      console.error('Wake lock error:', err);
    } finally {
      this.isWakeLockRequestInFlight = false;
    }
  }

  releaseWakeLock(): void {
    this.shouldKeepWakeLock = false;
    this.stopWakeLockTimer();

    if (this.wakeLock) {
      const releasedAt = new Date().toLocaleTimeString();
      const activeTimeText = this.wakeLockTimerText;
      const releasePromise = this.wakeLock.release();
      this.wakeLock = null;
      this.wakeLockTimerText = '00:00';
      this.wakeLockStatusText = `Wake lock released at ${releasedAt}`;
      console.log(`[WakeLock] released manually at ${releasedAt}. Total active time: ${activeTimeText}`);
      void releasePromise.catch(() => undefined);
      return;
    }

    this.wakeLockStatusText = 'Wake lock already released';
    this.wakeLockTimerText = '00:00';
  }

  setRecordingActive(active: boolean): void {
    this.shouldKeepWakeLock = active;

    if (active) {
      void this.requestWakeLock();
      return;
    }

    this.releaseWakeLock();
  }
}
